<template>
  <div id="ticketEdit">
    <div class="container">

      <PageTitle :title="ticket && $to(ticket.name) ? $to(ticket.name) : $t('ticket.single')">
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanSaveInventory">
          {{ $t("save") }}
        </button>
      </PageTitle>

      <template v-if="ticket">
        <Tabs>
          <TabSection :title="$t('categories.information')">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <LocalizedInput :title="$t('name')" :translate="ticket.name" :validate="$validate"
                    errorsKey="ticket.name" />
                </div>
                <div class="group">
                  <LocalizedInput textarea :title="$t('description')" :translate="ticket.description" />
                </div>
                <div class="group" v-for="(priceScheme, priceSchemeIndex) in ticket.priceSchemes" :key="priceSchemeIndex">
                  <label>{{ $t(`scheme_methods.${priceScheme.method}`) }} ({{ currencySymbol }})</label>
                  <input type="number" v-model="priceScheme.unitPrice" step="0.01"
                    :class="{ invalid: $validate.error('ticket.priceSchemes[' + priceSchemeIndex + '].unitPrice') }">
                </div>
                <div class="group">
                  <label>{{ $t('items.taxable') }}</label>
                  <select v-model="ticket.taxable">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('items.out_of_stock') }}</label>
                  <select v-model="ticket.outOfStock">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('ticket.unique_code') }}</label>
                  <input type="text" v-model="ticket.moreData.code"
                    :class="{ 'invalid': $validate.error('ticket.moreData.code') }">
                </div>
                <div class="group">
                  <ImageUpload identifier="ticketImageUpload" :object="ticket.image" @changed="ticket.image = $event"
                    :maxSizeInMb="0.8"></ImageUpload>
                </div>
              </div>
            </div>
          </TabSection>

          <TabSection :title="$t('ticket.disclaimer')">
            <div class="group">
              <LocalizedInput :title="$t('ticket.disclaimer_title')" :translate="ticket.moreData.disclaimerTitle" />
            </div>
            <div class="group">
              <LocalizedInput textarea :title="$t('ticket.disclaimer_description')"
                :translate="ticket.moreData.disclaimerDescription" />
            </div>
            <div class="group">
              <input id="chk_showDisclaimerInPopup" type="checkbox" v-model="ticket.moreData.disclaimerPopupActivated">
              <label for="chk_showDisclaimerInPopup">{{ $t('ticket.activate_disclaimer_popup') }}</label>
            </div>
          </TabSection>
        </Tabs>
      </template>

      <div class="footer-actions" v-if="$store.getters.loggedUserCanSaveInventory">
        <button class="btn btn-secondary" @click="back">{{ $t('back') }}</button>
        <div class="grow"></div>
        <button class="btn btn-primary" @click="save">{{ $t('save') }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ImageUpload from "@/components/tools/ImageUpload.vue";
  import PageTitle from "@/components/PageTitle";

  export default {
    name: "ticketEdit",

    components: { LocalizedInput, ImageUpload, PageTitle },

    data() {
      return {
        currencySymbol: "",
        company: null,
        inventory: null,
        ticket: {
          name: { fr: "", en: "" },
          class: "aims",
          description: { fr: "", en: "" },
          image: { url: "" },
          priceSchemes: [
            { method: "digital", unitPrice: 0 }
          ],
          taxable: true,
          outOfStock: false,
          moreData: {
            code: "",
            disclaimerPopupActivated: false,
            disclaimerTitle: { fr: "", en: "" },
            disclaimerDescription: { fr: "", en: "" }
          }
        }
      }
    },

    computed: {
      ticketId() {
        return this.$route.query.id;
      },
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "ticket.single";
      this.$store.state.pageIcon = "/icon/ticket.svg";

      showSpinner();

      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.inventory = await this.$store.getters.getContextInventory;
      this.company = await this.$store.getters.getContextCompany;
      if (this.ticketId) {
        await this.loadTicket();
        await this.loadTicketImage();
      }

      if (this.$store.getters.isBranchContextInventory) {
        window.lockFields(this.$store.getters.getOverloadableFields);
      } else if (!this.$store.getters.loggedUserCanModifyInventory) {
        window.lockFields();
      }

      hideSpinner();
    },

    validate: {
      'ticket.name.fr': {
        required: true,
        condition: function () {
          return !this.ticket.name.en;
        }
      },
      'ticket.name.en': {
        required: true,
        condition: function () {
          return !this.ticket.name.fr;
        }
      },
      "ticket.moreData.code": {
        required: true,
        length: 3
      },
      "ticket.priceSchemes": {
        array: true,
        elements: {
          unitPrice: {
            required: true,
            maxDecimals: 2
          }
        }
      }
    },

    methods: {
      ...mapActions(["getCustomItem", "saveCustomItem", "createCustomItem", "saveImage", "getImage"]),

      back() {
        this.$router.push(this.$router.buildContext("/aims-tickets"));
      },

      async loadTicket() {
        if (this.ticketId) {
          let response = await this.getCustomItem({ inventoryId: this.inventory.id, itemId: this.ticketId });
          if (response) {
            response.priceSchemes = response.priceSchemes.filter(p => p.method === "digital");
            this.ticket = response;
            if (!this.ticket.moreData.code) {
              this.ticket.moreData.code = "";
            }
          }
        }
      },

      async loadTicketImage() {
        if (this.ticket.image) {
          this.ticket.image = await this.getImage({ inventoryId: this.inventory.id, imageId: this.ticket.image });
        }
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }
        showSpinner();
        let data = await this._formatBeforeSave();
        let response;
        if (this.ticketId) {
          response = await this.saveCustomItem({ inventoryId: this.inventory.id, itemId: this.ticketId, data: data });
        } else {
          response = await this.createCustomItem({ inventoryId: this.inventory.id, data: data });
        }
        if (!response) {
          toast(this.$t("error.no_server"), "error");
          hideSpinner();
          return;
        }
        this.ticket = response;
        if (!this.ticketId) {
          this.$router.push(this.$router.buildContext("/aims-ticket/edit", { id: this.ticket.id }));
        }
        hideSpinner();
        toast(this.$t("quick_company.save_success"), "success", this.$t("success"));
      },

      async _formatBeforeSave() {
        let data = this._copy(this.ticket);
        data.moreData.code = data.moreData.code.toUpperCase();
        await this._formatImageBeforeSave(data, 'image');
        return data;
      },

      async _formatImageBeforeSave(obj, fieldName) {
        if (obj[fieldName] && obj[fieldName].url && obj[fieldName].url.startsWith("data:")) {
          let image = await this.saveImage({ inventoryId: this.inventory.id, image: getImageData(obj[fieldName]) });
          obj[fieldName] = image ? image.id : null;
        } else if (obj[fieldName] && obj[fieldName].id) {
          obj[fieldName] = obj[fieldName].id;
        } else if (obj[fieldName] && obj[fieldName].deleted) {
          obj[fieldName] = null;
        }
        return obj;
      },

      _copy(obj) {
        return JSON.parse(JSON.stringify(obj));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #ticketEdit {
    display: flex;
  }
</style>
